import React, { useMemo } from "react";

import { Stack } from "@mui/system";
import Decimal from "decimal.js";
import { orderBy } from "lodash";

import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";
import { Response as SecResponse } from "@fartherfinance/frontend/api/SecurityMaster/requests/getSecuritiesBySecurityIds";
import { SecurityId } from "@fartherfinance/frontend/api/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import { BorderedRow } from "@src/sharedComponents/List/BorderedList";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Text from "@src/sharedComponents/Typography/Text";
import displayTitleFromSecurity from "@src/utils/displayTitleFromSecurity";

import SubtleSpan from "./SubtleSpan";

const NUM_LOADING_SECURITIES = 5;

interface SecurityProps {
  sortedBy: "name" | "weight";
  securities: {
    securityId: SecurityId;
    weight: Decimal;
  }[];
}

type MergeSecurityData = {
  security: SecResponse[number];
  weight: Decimal;
}[];

const SecurityRows: React.FC<SecurityProps> = ({ securities, sortedBy }) => {
  const auth = useRequestAuth();
  const advisorAuth = useAdvisorRequestAuth();

  const secIds = useMemo(
    () => securities.map((s) => s.securityId),
    [securities]
  );
  const securitiesData = useSecuritiesBySecurityIds(
    secIds,
    auth ?? advisorAuth
  );

  const combinedSecurityData: undefined | MergeSecurityData = useMemo(() => {
    if (securitiesData.data === undefined) {
      return undefined;
    }

    return securities.map((sec) => {
      const securityFound = securitiesData.data?.find(
        (s) => s.id === sec.securityId
      );

      if (securityFound === undefined) {
        throw new Error("Security not found");
      }

      return {
        security: securityFound,
        weight: sec.weight,
      };
    });
  }, [securities, securitiesData.data]);

  const sortedSecuritiesArray = useMemo(() => {
    switch (sortedBy) {
      case "name":
        return orderBy(
          combinedSecurityData,
          (sec) =>
            // we don't expect name to be duplicated, so sorting only by tickers is fine
            sec.security.identifiers.find((s) => s.registrar === "TICKER")
              ?.identifier ?? sec.security.name,
          ["asc"]
        );
      case "weight":
        return orderBy(
          combinedSecurityData,
          [
            (sec) => sec.weight.toNumber(),
            (sec) =>
              sec.security.identifiers.find((s) => s.registrar === "TICKER")
                ?.identifier ?? sec.security.name,
          ],
          ["desc", "asc"]
        );
    }
  }, [combinedSecurityData, sortedBy]);

  if (securitiesData.isLoading) {
    return (
      <>
        {new Array(NUM_LOADING_SECURITIES).fill(null).map((_, i) => (
          <BorderedRow
            key={i}
            left={
              <Stack direction="row" spacing={2}>
                <Skeleton width={30} /> <Skeleton width={100} />
              </Stack>
            }
            right={<Skeleton width={30} />}
          />
        ))}
      </>
    );
  }

  if (securitiesData.hasError) {
    return (
      <Text variant="body" style={{ textAlign: "center", padding: 8 }}>
        Error loading Securities
      </Text>
    );
  }

  return (
    <>
      {sortedSecuritiesArray.map((sec) => {
        const identifierToShow = displayTitleFromSecurity(sec.security);

        const description =
          sec.security.description ?? sec.security.name ?? "N/A";

        const renderDescription = identifierToShow !== description;

        return (
          <BorderedRow
            key={sec.security.id}
            left={
              <div>
                <span>{identifierToShow}</span>{" "}
                {renderDescription ? (
                  <SubtleSpan style={{ paddingLeft: 6 }}>
                    {description}
                  </SubtleSpan>
                ) : null}
              </div>
            }
            right={sec.weight.toNumber().toLocaleString("en-US", {
              style: "percent",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          />
        );
      })}
    </>
  );
};

export default SecurityRows;
